import { useEffect, useMemo, useState } from "react";
import { Loader2, Lock, Plus, Save, Trash2, ShieldCheck, CreditCard, Users, MapPin, UserPlus, RefreshCw, Landmark, Mail, Copy, Search, X, Award, BarChart3, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import PlanSentChip from "./PlanSentChip";

const inputCls = "w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none";
const labelCls = "text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55";

// Human "how long we've worked together" from an ISO start date.
function tenureLabel(iso) {
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return "";
  const days = Math.max(0, Math.floor((Date.now() - start.getTime()) / 86400000));
  if (days < 1) return "Today";
  if (days < 31) return `${days} day${days === 1 ? "" : "s"}`;
  const months = Math.floor(days / 30.44);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"}`;
  const years = Math.floor(months / 12);
  const remM = months % 12;
  return `${years} yr${years === 1 ? "" : "s"}${remM ? ` ${remM} mo` : ""}`;
}

const qFileUrl = (path, filename) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/admin/questionnaire-file/download?path=${encodeURIComponent(path)}&filename=${encodeURIComponent(filename || "file")}`;

// Auto-flag anniversary milestones from an ISO start date. `fresh` is true when
// a milestone was crossed within the last 30 days (worth celebrating now).
function milestone(iso) {
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return null;
  const days = Math.floor((Date.now() - start.getTime()) / 86400000);
  const tiers = [
    { d: 3650, label: "10-Year Client" }, { d: 1825, label: "5-Year Client" },
    { d: 1095, label: "3-Year Client" }, { d: 730, label: "2-Year Client" },
    { d: 365, label: "1-Year Client" }, { d: 183, label: "6-Month Client" },
    { d: 90, label: "90-Day Client" },
  ];
  const hit = tiers.find((t) => days >= t.d);
  if (!hit) return null;
  return { label: hit.label, fresh: days - hit.d <= 30 };
}

import AdminAttachmentReview from "./AdminAttachmentReview";
import SavingsComposer from "./SavingsComposer";
import ComposeAdvisorRequest from "./ComposeAdvisorRequest";
import SavingsTimeline from "./SavingsTimeline";
import PortalOperations from "./PortalOperations";

const EMPTY = {
  client_id: "",
  business_name: "",
  primary_contact: { name: "", role: "", email: "", phone: "" },
  additional_contacts: [],
  addresses: [],
  referrals: [],
  payment_methods_on_file: [],
  bank_accounts_on_file: [],
  stripe_customer_id: "",
  identity_verified: false,
  notes: "",
};

export default function ClientVault({ onSendPlan = null, planSent = {}, onLaunchAssessment = null }) {
  const planFor = (r) => planSent[(r.primary_contact?.email || r.client_id || "").toLowerCase()] || planSent[(r.client_id || "").toLowerCase()];
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [assessments, setAssessments] = useState({});
  const [assessBusy, setAssessBusy] = useState(null);
  useEffect(() => {
    api.get("/expenses/business-intelligence/overview").then(({ data }) => {
      const map = {};
      (data.clients || []).forEach((c) => {
        if (c.client_id) map[c.client_id.toLowerCase()] = c;
        if (c.business_name) map[`bn:${c.business_name.trim().toLowerCase()}`] = c;
      });
      setAssessments(map);
    }).catch(() => {});
  }, []);
  const assessmentFor = (r) => assessments[(r.client_id || "").toLowerCase()]
    || assessments[`bn:${(r.business_name || "").trim().toLowerCase()}`] || null;
  const downloadAssessment = async (a) => {
    if (!a?.id) return;
    setAssessBusy(a.id);
    try {
      const res = await api.get(`/expenses/business-intelligence/${a.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const el = document.createElement("a");
      el.href = url;
      el.download = `elevare-bi-${(a.business_name || "client").replace(/\s+/g, "-").toLowerCase()}.pdf`;
      el.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download that assessment."); }
    finally { setAssessBusy(null); }
  };
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [connectingBank, setConnectingBank] = useState(false);
  const [bankBalances, setBankBalances] = useState([]);
  const [balancesLoading, setBalancesLoading] = useState(false);
  // CRM search — filters the client list by name, email, or phone number.
  // Kept fully client-side so it stays instant even on hundreds of rows.
  const [query, setQuery] = useState("");
  const [uploadedDocs, setUploadedDocs] = useState([]); // portal-uploaded files (progress + records requests)

  const load = () => {
    api.get("/admin/vault")
      .then(({ data }) => setRows(data.vault || []))
      .catch(() => toast.error("Could not load CRM records."));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setOpenId("__new"); setDraft(EMPTY); setUploadedDocs([]); };
  const openRow = (r) => {
    setOpenId(r.client_id);
    setDraft({ ...EMPTY, ...r, primary_contact: r.primary_contact || EMPTY.primary_contact });
    // Load banking snapshot for this client so the owner can peek at balances.
    setBankBalances([]);
    setBalancesLoading(true);
    api.get(`/admin/vault/${encodeURIComponent(r.client_id)}/banking`)
      .then(({ data }) => setBankBalances(data?.accounts || []))
      .catch(() => {})
      .finally(() => setBalancesLoading(false));
    // Pull everything the client uploaded through their portal so every file
    // shows in one place inside their CRM folder.
    setUploadedDocs([]);
    api.get(`/admin/vault/${encodeURIComponent(r.client_id)}/uploaded-docs`)
      .then(({ data }) => setUploadedDocs(data?.documents || []))
      .catch(() => {});
  };
  const close = () => { setOpenId(null); setDraft(EMPTY); setUploadedDocs([]); };

  // --- Change a client's email everywhere (re-keys every collection) ---
  const [emailChangeOpen, setEmailChangeOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const changeClientEmail = async () => {
    const oldEmail = openId;
    const nw = newEmail.trim().toLowerCase();
    if (!nw || nw === oldEmail) { toast.error("Enter a different email address."); return; }
    setEmailBusy(true);
    try {
      const { data } = await api.post("/admin/clients/change-email", { old_email: oldEmail, new_email: nw });
      toast.success(`Email re-keyed across ${data.total_modified} record${data.total_modified === 1 ? "" : "s"}.`);
      setEmailChangeOpen(false); setNewEmail("");
      load();
      setOpenId(nw);
      setDraft((d) => ({ ...d, client_id: nw }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change email.");
    } finally {
      setEmailBusy(false);
    }
  };

  const setPC = (k, v) => setDraft((d) => ({ ...d, primary_contact: { ...d.primary_contact, [k]: v } }));
  const addRow = (key, blank) => setDraft((d) => ({ ...d, [key]: [...(d[key] || []), blank] }));
  const editRow = (key, idx, patch) => setDraft((d) => ({
    ...d, [key]: d[key].map((r, i) => (i === idx ? { ...r, ...patch } : r)),
  }));
  const rmRow = (key, idx) => setDraft((d) => ({ ...d, [key]: d[key].filter((_, i) => i !== idx) }));

  const save = async () => {
    const cid = (draft.client_id || "").trim().toLowerCase();
    if (!cid) { toast.error("Client ID (email) is required."); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/vault/${encodeURIComponent(cid)}`, { ...draft, client_id: cid });
      const promoted = data?.leads_created_from_referrals?.length || 0;
      if (promoted > 0) {
        toast.success(
          promoted === 1
            ? `Vault saved · ${data.leads_created_from_referrals[0].name} added to Leads`
            : `Vault saved · ${promoted} referrals promoted to Leads`,
        );
      } else {
        toast.success("Client record saved.");
      }
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const del = async (cid) => {
    if (!window.confirm(`Delete the CRM record for ${cid}?`)) return;
    try {
      await api.delete(`/admin/vault/${encodeURIComponent(cid)}`);
      toast.success("Client record deleted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const verifyIdentity = async (cid) => {
    setVerifying(true);
    try {
      const { data } = await api.post(`/admin/vault/${encodeURIComponent(cid)}/identity-verify`);
      if (data.url) {
        try { await navigator.clipboard.writeText(data.url); } catch { /* clipboard may be blocked */ }
        window.open(data.url, "_blank", "noopener");
      }
      if (data.invite_sent_to) {
        toast.success(`Identity link emailed to ${data.invite_sent_to} · also copied to clipboard.`);
      } else if (data.invite_error) {
        toast.warning(`${data.invite_error} · Link copied to clipboard.`);
      } else {
        toast.success("Identity session created — link copied to clipboard.");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Identity verify not available.");
    } finally {
      setVerifying(false);
    }
  };

  const checkIdentityStatus = async (cid) => {
    try {
      const { data } = await api.get(`/admin/vault/${encodeURIComponent(cid)}/identity-status`);
      toast.success(`Status: ${data.status || "unknown"}${data.verified ? " ✓" : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not refresh status.");
    }
  };

  const manualVerify = async (cid) => {
    if (!window.confirm("Mark this client as identity-verified without Stripe? Use only when the client has been verified out-of-band or Stripe test mode is stuck.")) return;
    try {
      await api.post(`/admin/vault/${encodeURIComponent(cid)}/identity/manual-verify`);
      toast.success("Marked as verified (manual override).");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manual verify failed.");
    }
  };

  const resetIdentity = async (cid) => {
    if (!window.confirm("Clear identity state so this client can start a fresh Stripe verification?")) return;
    try {
      await api.post(`/admin/vault/${encodeURIComponent(cid)}/identity/reset`);
      toast.success("Identity state cleared.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    }
  };

  const connectBank = async (cid) => {
    setConnectingBank(true);
    try {
      const { data } = await api.post(`/admin/vault/${encodeURIComponent(cid)}/banking-connect`);
      if (data.url) {
        try { await navigator.clipboard.writeText(data.url); } catch { /* noop */ }
        window.open(data.url, "_blank", "noopener");
      }
      if (data.invite_sent_to) {
        toast.success(`Bank-connect link emailed to ${data.invite_sent_to} · also copied.`);
      } else {
        toast.success("Bank connect session created — link copied to clipboard.");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bank connect not available (verify identity first).");
    } finally {
      setConnectingBank(false);
    }
  };

  const copyText = async (text, label = "Link") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Could not copy — select and copy manually.");
    }
  };

  // Only keep digits from a phone value so "(555) 123-4567" still matches "5551234567".
  const digits = (s) => (s || "").replace(/\D+/g, "");

  const filteredRows = useMemo(() => {
    const all = rows || [];
    const q = (query || "").trim().toLowerCase();
    if (!q) return all;
    const qDigits = digits(q);
    return all.filter((r) => {
      // Aggregate every searchable string on the record.
      const haystackText = [
        r.business_name,
        r.client_id,
        r.primary_contact?.name,
        r.primary_contact?.email,
        r.primary_contact?.role,
        ...(r.additional_contacts || []).flatMap((c) => [c.name, c.email, c.role]),
        // Addresses — label + street lines + city + state + postal + country
        ...(r.addresses || []).flatMap((a) => [
          a.label, a.line1, a.line2, a.city, a.state, a.postal, a.country,
        ]),
        // Referrals — name, business, email, role, notes
        ...(r.referrals || []).flatMap((rf) => [
          rf.name, rf.business_name, rf.email, rf.role, rf.notes,
        ]),
      ].filter(Boolean).join(" ").toLowerCase();

      // Aggregate every phone-shaped value for digit-only matching (555-1234 vs 5551234).
      const haystackDigits = [
        r.primary_contact?.phone,
        ...(r.additional_contacts || []).map((c) => c.phone),
        ...(r.referrals || []).map((rf) => rf.phone),
        // Also match on postal codes typed as digits (zip: 90210)
        ...(r.addresses || []).map((a) => a.postal),
      ].map(digits).filter(Boolean).join(" ");

      if (haystackText.includes(q)) return true;
      if (qDigits.length >= 3 && haystackDigits.includes(qDigits)) return true;
      return false;
    });
  }, [rows, query]);

  return (
    <div data-testid="tab-content-vault" className="space-y-5">
      {/* Header + safety banner */}
      <div className="rounded-2xl border border-[#c9a961]/40 bg-[#c9a961]/8 px-5 py-3 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">CRM</p>
            <h2 data-testid="vault-header-title" className="font-head text-lg text-[#1e3d3a] leading-tight">Client Records Management</h2>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="vault-refresh" onClick={load} title="Refresh"
                    className="text-[#1e3d3a]/55 hover:text-[#a3823f] transition-colors duration-300">
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            <button data-testid="vault-add" onClick={openNew}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300">
              <Plus className="h-3.5 w-3.5" /> New client record
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[#1e3d3a]/70">
          <Lock className="h-3.5 w-3.5 text-[#a3823f] shrink-0" />
          <span>Every read/write is audit-logged. No raw card, bank, or SSN data lives here — Stripe holds those.</span>
        </div>
        {/* Search bar — name, email, or phone */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#1e3d3a]/40 pointer-events-none" />
          <input
            data-testid="vault-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, phone, address, or referral…"
            className="w-full text-xs rounded-full border border-[#1e3d3a]/15 bg-white/80 pl-9 pr-9 py-2 focus:border-[#c9a961] focus:outline-none placeholder:text-[#1e3d3a]/40"
          />
          {query && (
            <button
              data-testid="vault-search-clear"
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#1e3d3a]/40 hover:text-[#b23a48] p-1 transition-colors duration-300"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {query && (
          <div data-testid="vault-search-count" className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50">
            {filteredRows.length} {filteredRows.length === 1 ? "match" : "matches"} for "{query}"
          </div>
        )}
      </div>

      {/* List */}
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white overflow-hidden">
        {rows === null && <div className="p-6 text-xs text-[#1e3d3a]/55"><Loader2 className="h-4 w-4 inline animate-spin" /> Loading…</div>}
        {rows !== null && rows.length === 0 && <div data-testid="vault-empty" className="p-6 text-xs text-[#1e3d3a]/55">No client records yet. Click "New client record" to add one.</div>}
        {rows !== null && rows.length > 0 && filteredRows.length === 0 && (
          <div data-testid="vault-search-empty" className="p-6 text-xs text-[#1e3d3a]/55">
            No client matches "{query}". Try a partial name, email, or phone number.
          </div>
        )}
        {(filteredRows || []).map((r) => (
          <div key={r.client_id} data-testid={`vault-row-${r.client_id}`} className="border-b border-[#1e3d3a]/10 last:border-b-0">
            <div
              role="button"
              tabIndex={0}
              onClick={() => openRow(r)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openRow(r);
                }
              }}
              className="p-4 flex flex-wrap items-center justify-between gap-2 hover:bg-[#c9a961]/8 focus-visible:bg-[#c9a961]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a961]/40 cursor-pointer transition-colors duration-300"
            >
              <div className="min-w-0 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-[#1e3d3a]/6 grid place-items-center text-[#1e3d3a]">
                  <Users className="h-4 w-4" strokeWidth={1.6} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#1e3d3a] truncate flex items-center gap-2">
                    {(() => {
                      const a = assessmentFor(r);
                      const cov = a?.coverage;
                      const color = cov == null ? "#c7c7c7" : cov >= 80 ? "#6b7c3a" : cov >= 50 ? "#c9a961" : "#b23a48";
                      const label = cov == null ? "No assessment yet" : `Assessment health: ${cov}% coverage`;
                      return <span data-testid={`vault-health-${r.client_id}`} title={label} className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: color }} />;
                    })()}
                    <span className="truncate">{r.primary_contact?.name || r.business_name || r.client_id}</span>
                    <PlanSentChip req={planFor(r)} testid={`vault-plan-chip-${r.client_id}`} />
                    {r.engagement_signed && (
                      <span
                        data-testid={`vault-engagement-chip-${r.client_id}`}
                        title="Engagement authorization signed"
                        className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#16a34a] text-white px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] shadow-sm"
                      >
                        <ShieldCheck className="h-3 w-3" /> Signed
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-[#1e3d3a]/55 truncate">
                    {r.business_name && r.primary_contact?.name && <>{r.business_name} · </>}
                    {r.client_id}
                    {r.identity_verified && <> · <ShieldCheck className="h-3 w-3 inline text-[#6b7c3a]" /> Identity verified</>}
                    {r.stripe_customer_id && <> · Stripe {r.stripe_customer_id.slice(0, 12)}</>}
                  </div>
                </div>
              </div>
              {/* Action pills — stop-propagation so clicking the delete
                  button doesn't ALSO open the drawer. */}
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                {onSendPlan && (
                  <button data-testid={`vault-send-plan-${r.client_id}`}
                          onClick={() => onSendPlan({ name: r.primary_contact?.name || "", email: r.primary_contact?.email || r.client_id, business_name: r.business_name || "" })}
                          title={planFor(r) ? "A payment link is already out — send another only if needed" : "Send this client a plan + payment link"}
                          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2 py-1 transition-colors duration-300 ${planFor(r) ? "text-[#1e3d3a]/30 hover:text-[#1e3d3a]/60" : "text-[#1e3d3a]/60 hover:text-[#a3823f]"}`}>
                    <CreditCard className="h-3.5 w-3.5" /> {planFor(r) ? "Resend plan" : "Send plan"}
                  </button>
                )}
                <button data-testid={`vault-open-${r.client_id}`} onClick={() => openRow(r)}
                        className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/60 hover:text-[#a3823f] px-2 py-1 transition-colors duration-300">
                  Open
                </button>
                <button data-testid={`vault-del-${r.client_id}`} onClick={() => del(r.client_id)}
                        className="text-[#1e3d3a]/40 hover:text-[#b23a48] px-2 py-1 transition-colors duration-300">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Editor drawer */}
      {openId && (
        <div data-testid="vault-editor" className="rounded-2xl border border-[#c9a961]/40 bg-white p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961]">
              {openId === "__new" ? "New client record" : (draft.primary_contact?.name || draft.business_name || openId)}
            </div>
            {openId !== "__new" && (draft.primary_contact?.name || draft.business_name) && (
              <div className="text-[10px] text-[#1e3d3a]/45 mt-0.5">
                {draft.business_name && draft.primary_contact?.name ? `${draft.business_name} · ` : ""}{openId}
              </div>
            )}
            <button onClick={close} className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55 hover:text-[#b23a48]">Close</button>
          </div>

          {/* Basic */}
          {draft.created_at && (
            <div data-testid="vault-tenure" className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-[#1e3d3a]/12 bg-[#f5f3ec] px-4 py-2.5 text-xs text-[#1e3d3a]/70">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#a3823f]">Client since</span>
              <span className="font-medium text-[#1e3d3a]">
                {new Date(draft.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
              </span>
              <span className="text-[#1e3d3a]/45">·</span>
              <span>{tenureLabel(draft.created_at)} working together</span>
              {milestone(draft.created_at) && (
                <span data-testid="vault-milestone"
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.14em] ${
                    milestone(draft.created_at).fresh ? "bg-[#c9a961] text-[#1e3d3a]" : "bg-[#1e3d3a]/8 text-[#1e3d3a]/70"}`}>
                  <Award className="h-3 w-3" />{milestone(draft.created_at).label}{milestone(draft.created_at).fresh ? " · new!" : ""}
                </span>
              )}
            </div>
          )}

          {/* Business Intelligence Assessment (embedded per client) */}
          {openId !== "__new" && (() => {
            const a = assessmentFor(draft);
            return (
              <div data-testid="vault-assessment" className="rounded-xl border border-[#c9a961]/30 bg-[#c9a961]/5 px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="h-3.5 w-3.5 text-[#c9a961]" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/60">Client assessment</span>
                </div>
                {a ? (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-[#1e3d3a]">
                        {a.coverage}% coverage <span className="text-[11px] text-[#1e3d3a]/50">· {a.analyzed}/{a.total} items</span>
                      </div>
                      <div className="text-[10px] text-[#1e3d3a]/45">
                        Latest {a.created_at ? new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {onLaunchAssessment && (
                        <button type="button" data-testid="vault-assessment-new" onClick={() => onLaunchAssessment(draft)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 text-[#1e3d3a] px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300">
                          <RefreshCw className="h-3 w-3" /> New
                        </button>
                      )}
                      <button type="button" data-testid="vault-assessment-download" onClick={() => downloadAssessment(a)} disabled={assessBusy === a.id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3.5 py-1.5 text-[10px] uppercase tracking-[0.16em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-50 transition-colors duration-300">
                        {assessBusy === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <p data-testid="vault-assessment-empty" className="text-[11px] text-[#1e3d3a]/50">
                      No assessment yet for this client.
                    </p>
                    {onLaunchAssessment && (
                      <button type="button" data-testid="vault-assessment-new" onClick={() => onLaunchAssessment(draft)}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3.5 py-1.5 text-[10px] uppercase tracking-[0.16em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 shrink-0">
                        <BarChart3 className="h-3.5 w-3.5" /> Run assessment
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Basic */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Client ID (email)</label>
              <input data-testid="vault-client-id" className={inputCls}
                     value={draft.client_id} onChange={(e) => setDraft({ ...draft, client_id: e.target.value })}
                     disabled={openId !== "__new"} placeholder="client@example.com" />
              {openId !== "__new" && (
                !emailChangeOpen ? (
                  <button type="button" data-testid="vault-change-email-open"
                    onClick={() => { setEmailChangeOpen(true); setNewEmail(""); }}
                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#a3823f] hover:text-[#1e3d3a] transition-colors duration-300">
                    <Mail className="h-3 w-3" /> Change email everywhere
                  </button>
                ) : (
                  <div className="mt-2 rounded-lg border border-[#c9a961]/40 bg-[#c9a961]/8 p-2.5">
                    <p className="text-[10px] text-[#1e3d3a]/70 mb-1.5">Re-keys this client across every document (vault, reports, bookings, payments, progress, authorizations, files).</p>
                    <input data-testid="vault-change-email-input" type="email" value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)} placeholder="new@example.com"
                      className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none" />
                    <div className="flex items-center gap-1.5 mt-2">
                      <button type="button" data-testid="vault-change-email-confirm" onClick={changeClientEmail} disabled={emailBusy}
                        className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300">
                        {emailBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} Re-key
                      </button>
                      <button type="button" onClick={() => { setEmailChangeOpen(false); setNewEmail(""); }}
                        className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55 hover:text-[#1e3d3a] px-2 py-1 transition-colors duration-300">Cancel</button>
                    </div>
                  </div>
                )
              )}
            </div>
            <div>
              <label className={labelCls}>Business name</label>
              <input data-testid="vault-business" className={inputCls}
                     value={draft.business_name || ""} onChange={(e) => setDraft({ ...draft, business_name: e.target.value })} />
            </div>
          </div>

          {/* Business Health Questionnaire — carried over from the lead on conversion */}
          {draft.questionnaire && (draft.questionnaire.pdf || (draft.questionnaire.documents || []).length > 0) && (
            <div data-testid="vault-questionnaire" className="rounded-xl border border-[#c9a961]/35 bg-[#f5f3ec] p-4">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Business Health Questionnaire</div>
              {draft.questionnaire.score && (
                <div data-testid="vault-q-score" className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-3 py-1 text-xs">
                  <span className="font-head text-sm">{draft.questionnaire.score.overall_score}/100</span>
                  <span className="text-[10px] uppercase tracking-[0.16em] text-[#c9a961]">{draft.questionnaire.score.band}</span>
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {draft.questionnaire.pdf && (
                  <button type="button" data-testid="vault-q-pdf"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/questionnaire-file/download?path=${encodeURIComponent(draft.questionnaire.pdf.path)}&filename=${encodeURIComponent(draft.questionnaire.pdf.filename || "questionnaire.pdf")}`, "_blank")}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300">
                    Completed questionnaire PDF
                  </button>
                )}
                {(draft.questionnaire.documents || []).map((d) => (
                  <button key={d.id} type="button" data-testid={`vault-q-doc-${d.id}`}
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/questionnaire-file/download?path=${encodeURIComponent(d.storage_path)}&filename=${encodeURIComponent(d.filename)}`, "_blank")}
                    title={d.doc_type}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-[10px] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300">
                    <span className="max-w-[160px] truncate">{d.doc_type}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Client documents — questionnaire + expense PDFs + everything the
              client uploaded via their portal (progress + records requests). */}
          {(() => {
            const q = draft.questionnaire || {};
            const allDocs = [
              ...(q.pdf ? [{ storage_path: q.pdf.path, filename: q.pdf.filename || "questionnaire.pdf", source: "Questionnaire" }] : []),
              ...((q.documents || []).map((d) => ({ storage_path: d.storage_path, filename: d.filename, source: d.doc_type || "Questionnaire doc" }))),
              ...((draft.expense_documents || []).map((d) => ({ storage_path: d.storage_path, filename: d.filename, uploaded_at: d.uploaded_at, source: "Expense report" }))),
              ...((uploadedDocs || []).map((d) => ({ storage_path: d.path, filename: d.filename, uploaded_at: d.uploaded_at, source: d.source }))),
            ];
            if (allDocs.length === 0) return null;
            return (
            <div data-testid="vault-documents" className="rounded-xl border border-[#1e3d3a]/12 bg-white p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Client Documents</div>
                <button type="button" data-testid="vault-docs-download-all"
                  onClick={() => allDocs.forEach((d, i) =>
                    setTimeout(() => window.open(qFileUrl(d.storage_path, d.filename), "_blank"), i * 400))}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300">
                  Download all ({allDocs.length})
                </button>
              </div>
              <div className="mt-3 space-y-1.5">
                {allDocs.map((d, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 border-b border-[#1e3d3a]/8 pb-1.5">
                    <span className="text-xs text-[#1e3d3a]/75 truncate min-w-0">
                      {d.source && <span className="inline-block mr-2 rounded-full bg-[#c9a961]/15 text-[#a3823f] px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] align-middle">{d.source}</span>}
                      {d.filename}{d.uploaded_at ? ` · ${new Date(d.uploaded_at).toLocaleDateString()}` : ""}
                    </span>
                    <button type="button" data-testid={`vault-doc-${i}`}
                      onClick={() => window.open(qFileUrl(d.storage_path, d.filename), "_blank")}
                      className="shrink-0 rounded-full border border-[#1e3d3a]/15 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-[#1e3d3a]/70 hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300">
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
            );
          })()}


          {/* Primary contact */}
          <div>
            <div className={labelCls + " mb-2"}>Primary contact</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input data-testid="vault-pc-name" className={inputCls} placeholder="Name"
                     value={draft.primary_contact?.name || ""} onChange={(e) => setPC("name", e.target.value)} />
              <input className={inputCls} placeholder="Role (e.g. Owner)"
                     value={draft.primary_contact?.role || ""} onChange={(e) => setPC("role", e.target.value)} />
              <input className={inputCls} placeholder="Email"
                     value={draft.primary_contact?.email || ""} onChange={(e) => setPC("email", e.target.value)} />
              <input data-testid="vault-pc-phone" className={inputCls} placeholder="Phone"
                     value={draft.primary_contact?.phone || ""} onChange={(e) => setPC("phone", e.target.value)} />
            </div>
          </div>

          {/* Addresses */}
          <RepeatSection title="Addresses" icon={MapPin} items={draft.addresses}
            onAdd={() => addRow("addresses", { label: "", line1: "", line2: "", city: "", state: "", postal: "", country: "US" })}
            onRemove={(i) => rmRow("addresses", i)}
            render={(a, i) => (
              <div className="grid grid-cols-2 gap-2">
                <input className={inputCls} placeholder="Label (HQ / Billing)"
                       value={a.label || ""} onChange={(e) => editRow("addresses", i, { label: e.target.value })} />
                <input className={inputCls} placeholder="Country"
                       value={a.country || ""} onChange={(e) => editRow("addresses", i, { country: e.target.value })} />
                <input className={inputCls + " col-span-2"} placeholder="Line 1"
                       value={a.line1 || ""} onChange={(e) => editRow("addresses", i, { line1: e.target.value })} />
                <input className={inputCls + " col-span-2"} placeholder="Line 2"
                       value={a.line2 || ""} onChange={(e) => editRow("addresses", i, { line2: e.target.value })} />
                <input className={inputCls} placeholder="City"
                       value={a.city || ""} onChange={(e) => editRow("addresses", i, { city: e.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  <input className={inputCls} placeholder="State"
                         value={a.state || ""} onChange={(e) => editRow("addresses", i, { state: e.target.value })} />
                  <input className={inputCls} placeholder="Postal"
                         value={a.postal || ""} onChange={(e) => editRow("addresses", i, { postal: e.target.value })} />
                </div>
              </div>
            )} />

          {/* Additional contacts */}
          {openId !== "__new" && (
            <AdminAttachmentReview
              clientId={openId}
              approver={(typeof window !== "undefined" && localStorage.getItem("elevare_transfer_approver")) || "Kalib"}
            />
          )}
          {openId !== "__new" && (
            <SavingsComposer
              clientId={openId}
              defaultCreditedTo={(typeof window !== "undefined" && localStorage.getItem("elevare_transfer_approver")) || "Kalib"}
            />
          )}
          {openId !== "__new" && (
            <SavingsTimeline clientId={openId} />
          )}
          {openId !== "__new" && (
            <ComposeAdvisorRequest
              clientEmail={draft.primary_contact?.email || openId}
              clientName={draft.primary_contact?.name || draft.business_name}
            />
          )}

          {openId !== "__new" && (
            <div className="mt-2">
              <div className="mb-2 text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Operations / job costing</div>
              <PortalOperations clientId={openId} admin />
            </div>
          )}

          <RepeatSection title="Additional contacts" icon={Users} items={draft.additional_contacts}
            onAdd={() => addRow("additional_contacts", { name: "", role: "", email: "", phone: "" })}
            onRemove={(i) => rmRow("additional_contacts", i)}
            render={(c, i) => (
              <div className="grid grid-cols-2 gap-2">
                <input className={inputCls} placeholder="Name" value={c.name || ""} onChange={(e) => editRow("additional_contacts", i, { name: e.target.value })} />
                <input className={inputCls} placeholder="Role" value={c.role || ""} onChange={(e) => editRow("additional_contacts", i, { role: e.target.value })} />
                <input className={inputCls} placeholder="Email" value={c.email || ""} onChange={(e) => editRow("additional_contacts", i, { email: e.target.value })} />
                <input className={inputCls} placeholder="Phone" value={c.phone || ""} onChange={(e) => editRow("additional_contacts", i, { phone: e.target.value })} />
              </div>
            )} />

          {/* Referrals */}
          <RepeatSection title="Referrals (potential new clients)" icon={UserPlus} items={draft.referrals}
            onAdd={() => addRow("referrals", { name: "", business_name: "", email: "", phone: "", relationship: "", status: "introduced", notes: "" })}
            onRemove={(i) => rmRow("referrals", i)}
            render={(r, i) => (
              <div className="grid grid-cols-2 gap-2">
                <input className={inputCls} placeholder="Name" value={r.name || ""} onChange={(e) => editRow("referrals", i, { name: e.target.value })} />
                <input className={inputCls} placeholder="Business" value={r.business_name || ""} onChange={(e) => editRow("referrals", i, { business_name: e.target.value })} />
                <input className={inputCls} placeholder="Email" value={r.email || ""} onChange={(e) => editRow("referrals", i, { email: e.target.value })} />
                <input className={inputCls} placeholder="Phone" value={r.phone || ""} onChange={(e) => editRow("referrals", i, { phone: e.target.value })} />
                <input className={inputCls} placeholder="Relationship" value={r.relationship || ""} onChange={(e) => editRow("referrals", i, { relationship: e.target.value })} />
                <select className={inputCls} value={r.status || "introduced"} onChange={(e) => editRow("referrals", i, { status: e.target.value })}>
                  <option value="introduced">Introduced</option>
                  <option value="booked">Booked diagnostic</option>
                  <option value="signed">Signed on</option>
                  <option value="declined">Declined</option>
                </select>
                <textarea className={inputCls + " col-span-2"} rows={2} placeholder="Notes"
                          value={r.notes || ""} onChange={(e) => editRow("referrals", i, { notes: e.target.value })} />
              </div>
            )} />

          {/* Payment methods on file — labels only, no raw PAN */}
          <RepeatSection title="Payment methods on file (last 4 only)" icon={CreditCard} items={draft.payment_methods_on_file}
            onAdd={() => addRow("payment_methods_on_file", { brand: "Visa", last4: "", label: "", exp_month: null, exp_year: null })}
            onRemove={(i) => rmRow("payment_methods_on_file", i)}
            render={(p, i) => (
              <div className="grid grid-cols-2 gap-2">
                <select className={inputCls} value={p.brand || "Visa"} onChange={(e) => editRow("payment_methods_on_file", i, { brand: e.target.value })}>
                  {["Visa","Mastercard","Amex","Discover","Diners","JCB"].map((b) => <option key={b}>{b}</option>)}
                </select>
                <input className={inputCls} placeholder="Last 4" maxLength={4} value={p.last4 || ""} onChange={(e) => editRow("payment_methods_on_file", i, { last4: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
                <input className={inputCls + " col-span-2"} placeholder="Label — e.g. Business Amex, CFO card"
                       value={p.label || ""} onChange={(e) => editRow("payment_methods_on_file", i, { label: e.target.value })} />
              </div>
            )} />

          <RepeatSection title="Bank accounts on file (last 4 only)" icon={CreditCard} items={draft.bank_accounts_on_file}
            onAdd={() => addRow("bank_accounts_on_file", { brand: "ACH", last4: "", label: "" })}
            onRemove={(i) => rmRow("bank_accounts_on_file", i)}
            render={(b, i) => (
              <div className="grid grid-cols-2 gap-2">
                <input className={inputCls} placeholder="Bank name" value={b.brand || ""} onChange={(e) => editRow("bank_accounts_on_file", i, { brand: e.target.value })} />
                <input className={inputCls} placeholder="Last 4 of account" maxLength={4} value={b.last4 || ""} onChange={(e) => editRow("bank_accounts_on_file", i, { last4: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
                <input className={inputCls + " col-span-2"} placeholder="Label — e.g. Chase business checking"
                       value={b.label || ""} onChange={(e) => editRow("bank_accounts_on_file", i, { label: e.target.value })} />
              </div>
            )} />

          {/* Stripe + Identity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Stripe customer ID</label>
              <input className={inputCls} placeholder="cus_..."
                     value={draft.stripe_customer_id || ""}
                     onChange={(e) => setDraft({ ...draft, stripe_customer_id: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Identity verification (via Stripe Identity)</label>
              <div className="mt-1 flex items-center flex-wrap gap-2">
                <span className={`text-xs px-2 py-1 rounded-full ${draft.identity_verified ? "bg-[#6b7c3a]/12 text-[#4d5a24]" : "bg-[#1e3d3a]/6 text-[#1e3d3a]/55"}`}>
                  {draft.identity_verified ? <><ShieldCheck className="h-3 w-3 inline mr-1" /> Verified{draft.identity_verified_source === "manual_admin_override" ? " (manual)" : ""}</> : (draft.identity_status || "Not started")}
                </span>
                {openId !== "__new" && (
                  <>
                    <button type="button" data-testid="vault-verify"
                            onClick={() => verifyIdentity(openId)} disabled={verifying}
                            className="text-[10px] uppercase tracking-[0.2em] text-[#a3823f] hover:text-[#1e3d3a] disabled:opacity-60 inline-flex items-center gap-1">
                      <Mail className="h-3 w-3" /> {verifying ? "…" : (draft.identity_verified ? "Re-verify & email" : "Send verify link")}
                    </button>
                    <button type="button" data-testid="vault-verify-refresh"
                            onClick={() => checkIdentityStatus(openId)}
                            className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55 hover:text-[#a3823f] inline-flex items-center gap-1">
                      <RefreshCw className="h-3 w-3" /> Refresh
                    </button>
                    {draft.identity_url && (
                      <button type="button"
                              onClick={() => copyText(draft.identity_url, "Verify link")}
                              className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55 hover:text-[#a3823f] inline-flex items-center gap-1"
                              title="Copy last verify link">
                        <Copy className="h-3 w-3" /> Copy link
                      </button>
                    )}
                    {!draft.identity_verified && (
                      <button type="button" data-testid="vault-manual-verify"
                              onClick={() => manualVerify(openId)}
                              className="text-[10px] uppercase tracking-[0.2em] text-[#6b7c3a] hover:text-[#1e3d3a] inline-flex items-center gap-1"
                              title="Owner override — skip Stripe check">
                        <ShieldCheck className="h-3 w-3" /> Manual verify
                      </button>
                    )}
                    {(draft.identity_verified || draft.identity_status) && (
                      <button type="button" data-testid="vault-identity-reset"
                              onClick={() => resetIdentity(openId)}
                              className="text-[10px] uppercase tracking-[0.2em] text-[#b23a48]/70 hover:text-[#b23a48] inline-flex items-center gap-1"
                              title="Clear identity state so a fresh session can start">
                        Reset
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Secure banking via Financial Connections — unlocked once identity is verified */}
          {openId !== "__new" && (
            <div className="rounded-lg border border-[#c9a961]/30 bg-[#c9a961]/6 p-4">
              <div className={labelCls + " flex items-center gap-1.5 mb-2"}>
                <Landmark className="h-3 w-3" /> Secure banking · Stripe Financial Connections
              </div>
              {draft.identity_verified ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[11px] text-[#1e3d3a]/70 max-w-md">
                    Client can link a business bank account through Stripe. We only see brand + last 4 — no routing/account numbers ever hit our DB.
                  </p>
                  <button type="button" data-testid="vault-connect-bank"
                          onClick={() => connectBank(openId)} disabled={connectingBank}
                          className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300">
                    {connectingBank ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Landmark className="h-3.5 w-3.5" />}
                    Send bank-connect link
                  </button>
                </div>
              ) : (
                <p className="text-[11px] text-[#1e3d3a]/55 italic">Available once identity is verified above.</p>
              )}
              {bankBalances.length > 0 && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="vault-bank-balances">
                  {bankBalances.map((b) => {
                    const avail = b.balance?.available_cents;
                    const cur = b.balance?.current_cents;
                    const primary = avail ?? cur;
                    const usd = (c) => typeof c === "number"
                      ? `$${(c/100).toLocaleString(undefined,{maximumFractionDigits:0})}` : "—";
                    return (
                      <div key={b.id} className="rounded-lg bg-[#1e3d3a] text-white p-3">
                        <div className="text-[9px] uppercase tracking-[0.22em] text-[#c9a961]">
                          <Landmark className="h-3 w-3 inline mr-1" />
                          {b.brand} {b.last4 ? `••••${b.last4}` : ""}
                          {b.is_manual && <span className="ml-1 text-white/40">· manual</span>}
                        </div>
                        <div className="mt-1 text-xl font-head tabular-nums">
                          {primary != null ? usd(primary) : (b.is_manual ? "—" : "Sync…")}
                        </div>
                        {avail != null && cur != null && avail !== cur && (
                          <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">Ledger {usd(cur)}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {balancesLoading && <p className="text-[10px] text-[#1e3d3a]/45 mt-2">Loading balances…</p>}
            </div>
          )}

          <div>
            <label className={labelCls}>Notes</label>
            <textarea rows={3} className={inputCls} value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button data-testid="vault-save" onClick={save} disabled={saving}
                    className="inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-xs uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save record
            </button>
            <button onClick={close} className="text-xs uppercase tracking-[0.22em] text-[#1e3d3a]/55 hover:text-[#b23a48] px-3">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function RepeatSection({ title, icon: Icon, items, onAdd, onRemove, render }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#c9a961] flex items-center gap-1.5">
          <Icon className="h-3 w-3" /> {title}
        </div>
        <button onClick={onAdd} className="text-[10px] uppercase tracking-[0.2em] text-[#a3823f] hover:text-[#1e3d3a] inline-flex items-center gap-1">
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>
      {(items || []).length === 0 && <div className="text-[11px] text-[#1e3d3a]/45 italic">None yet.</div>}
      <ul className="space-y-3">
        {(items || []).map((it, i) => (
          <li key={i} className="rounded-lg border border-[#1e3d3a]/10 bg-[#eae7de]/30 p-3 relative">
            {render(it, i)}
            <button onClick={() => onRemove(i)} title="Remove"
                    className="absolute top-2 right-2 text-[#1e3d3a]/35 hover:text-[#b23a48] transition-colors duration-300">
              <Trash2 className="h-3 w-3" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
